import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import BaseScreenTable from '../../../../../../../baseComponents/TwinTable/BaseScreenTable'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { WorkSheetModelType } from '@teinor/erp/types/company/order/sell/workSheet'
import { CustomRenderString, DeleteRow } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { TwinTableLink } from '../../../../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import locations from '../../../../../../../utils/globals/locations'
import NullComponent from '../../../../../../../baseComponents/NullComponent'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { WorkSheetGroupStatusesPayload } from '../../../../../../../utils/reducers/sells/workSheetGroupStatus'
import { ButtonSecondary } from '../../../../../../../baseComponents/Button'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { useState } from 'react'
import WorkSheetsProductStatusView from './WorkSheetsProductStatusView'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { RenderWorkSheetStatus } from '../../subcomponents'
import ModalDeleteWorkSheet from './ModalDeleteWorkSheet'

type WorkSheetsProps = ComponentWithPermissions & ReduxWorkSheetGroupStatus & { }

const WorkSheets: React.FC<WorkSheetsProps> = ({ userPermissions, workSheetGroupStatuses }) => {
    const { t } = useTwinTranslation()
    const { view, setView } = useWorkSheetsLogic()
    const pageData = {
        title: t('workSheets', 'Hojas de trabajo'),
        newText: '',
    }
    if (view === 'product_status_view') {
        return <WorkSheetsProductStatusView setView={setView} />
    }

    return (
        <BaseScreenTable TableComponent={TableWorkSheets} CreateModalComponent={NullComponent} pageData={pageData} userPermissions={userPermissions} RightHeader={
            <ButtonChangeToStatusView setView={setView} workSheetGroupStatuses={workSheetGroupStatuses} />} DeleteModalComponent={ModalDeleteWorkSheet}/>
    )
}

const useWorkSheetsLogic = () => {
    const [view, setView] = useState<'default' | 'product_status_view'>('default')
    return { view, setView }
}

interface ButtonChangeToStatusViewProps {
    setView: (value: React.SetStateAction<"default" | "product_status_view">) => void
    workSheetGroupStatuses: WorkSheetGroupStatusesPayload
}

const ButtonChangeToStatusView: React.FC<ButtonChangeToStatusViewProps> = ({ setView, workSheetGroupStatuses }) => {
    if (!workSheetGroupStatuses || !Object.keys(workSheetGroupStatuses).length) {
        return null
    }
    return (
        <ButtonSecondary className='mr-5' onClick={() => setView('product_status_view')}>
            <TwinTrans transKey='changeView'>Cambiar vista</TwinTrans>
        </ButtonSecondary>)
}

type TableWorkSheetsProps = TableComponentProps & {}
const TableWorkSheets: React.FC<TableWorkSheetsProps> = ({ setTableInstance, setOpened }) => {
    const { t } = useTwinTranslation()
    const workSheetStatusDict: TwinDictionary = {
        0: { name: t('working', 'Trabajando'), id: 0 },
        1: { name: t('finished', 'Finalizado'), id: 1 },
    }

    const columns: ColumnTableSchema<WorkSheetModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 75
        },
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('assignedNumber', 'Nº asignado'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('assignedNumber', 'Nº asignado') }
            },
            searchKey: 'WorkSheet.full_assigned_number',
            minWidth: 130
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado') }
            },
            minWidth: 190,
            searchKey: 'WorkSheet.custom_name'
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderWorkSheetStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: workSheetStatusDict, label: t('status', 'Estado') }
            },
            searchKey: 'WorkSheet.status'
        },
        {
            id: 'SellOrderId',
            dataKey: 'SellOrderId',
            label: t('sellOrder', 'Pedido'),
            sortable: true,
            minWidth: 90,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.SellOrder?.full_assigned_number || parameterValue} />,
        },
        {
            id: 'SellDeliveryNoteId',
            dataKey: 'SellDeliveryNoteId',
            label: t('sellDeliveryNoteShort', 'Albarán'),
            sortable: true,
            minWidth: 90,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.SellDeliveryNote?.full_assigned_number || parameterValue} />,
        },
        {
            id: 'SellInvoiceId',
            dataKey: 'SellInvoiceId',
            label: t('sellInvoice', 'Factura'),
            sortable: true,
            minWidth: 90,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.SellInvoice?.full_assigned_number || parameterValue} />,
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.workSheetSingle, ['$id']]} name='workSheetsTable' columns={columns} getDataFrom='/api/app/order/sell/workSheet/getAllWorkSheets' setTableInstance={setTableInstance} />
    )
}

const workSheetGroupStatusesDispatch = {
    setWorkSheetGroupStatuses: (payload: WorkSheetGroupStatusesPayload) => ({ type: 'CHANGE_WORKSHEETGROUPSTATUSES', payload }),
}

export type ReduxWorkSheetGroupStatus = ConnectedProps<typeof workSheetGroupStatusConnect>
const mapWorkSheetGroupStatusConnector = (state: AllReduxPayloads) => ({ workSheetGroupStatuses: state.workSheetGroupStatuses })
const workSheetGroupStatusConnect = connect(mapWorkSheetGroupStatusConnector, workSheetGroupStatusesDispatch)
const workSheetGroupStatusConnectLoading = withLoading(WorkSheets, [{ fetchUrl: '/api/app/order/sell/workSheet/workSheetGroupStatus/getAllWorkSheetGroupStatusesComplete', propName: 'workSheetGroupStatuses', setFunctionName: 'setWorkSheetGroupStatuses' }])
const WorkSheetsConnect = workSheetGroupStatusConnect(workSheetGroupStatusConnectLoading)

export default WorkSheetsConnect
